import React, {useEffect, useMemo, useState} from "react";

import {Button, Col, Row, Spin} from "antd";
import {ArrowLeftOutlined, SyncOutlined} from '@ant-design/icons';
import {DollarIcon, StorageBoxOutlineIcon} from "@components/icons/Icons";
import {Contain} from "@components/layout";
import useSearchView from "@hooks/useSearchView";
import {useBoxesUpdateBoxMutation, useGetBoxByIdQuery, useRefreshBoxFmvMutation} from "@mwServices/Boxes";
import {formatDateTiny, formatGrade, formatMoney, formatNumber} from "@lib/format";
import {showResponse} from "@services/Api";
import {useNavigate} from "react-router-dom";
import MyCollectionIssues from "@mwPages/my-collection/issues/MyCollectionIssues";
import useSubscriptionType from "@hooks/useSubscriptionType";
import {FmvPrice} from "@components/fmv-price";
import moment from "moment";
import {scrollToTop} from "@lib/utils";
import {SCREEN, useWindowSize} from "@components/window-size-provider";
import {BoxesAddDrawer} from "@components/boxes-add-drawer";

const MyCollectionBoxIssues = () => {
    const windowDimensions = useWindowSize();
    const { view } = useSearchView();
    const navigate = useNavigate();

    const boxQuery = useGetBoxByIdQuery(view);
    const box = useMemo(() => {
        if(boxQuery.data && boxQuery.data.result){
            return boxQuery.data.result;
        }
        return null;
    }, [boxQuery]);

    const [callUpdateBox, updateBoxResponse] = useBoxesUpdateBoxMutation();
    const [postRefreshFmv, refreshFmvResponse] = useRefreshBoxFmvMutation();

    const subscriptionType = useSubscriptionType();
    const showTotalFmv = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const showStats = useMemo(() => (subscriptionType.isTrial || subscriptionType.isUnlimited), [subscriptionType]);
    const fmvUpdated = useMemo(() => box && box.fmv_updated ? moment(box.fmv_updated).toDate() : null, [box]);
    const [addBox, setAddBox] = useState<{show: boolean, issue_ids: number[], series_ids: number[]}>({show: false, issue_ids: [], series_ids: []});

    const iconStyle = useMemo(() => windowDimensions.up(SCREEN.MD) ? {color: '#2f404a', width: 50, height: 50, marginRight: 10} : {color: '#2f404a', width: 30, height: 30, marginRight: 0, padding: 5} , [windowDimensions])
    const iconClass = useMemo(() => windowDimensions.up(SCREEN.MD) ? "whiteCircleIconSmall" : "whiteCircleIconTiny", [windowDimensions]);

    const onSetBoxImage = async (issueId) => {
        let sanitisedData = {box_id: box.id, name: box.name, description: box.description, artwork_id: issueId};
        await callUpdateBox(sanitisedData);
    };

    useEffect(() => {
        showResponse(updateBoxResponse, 'Box updated', 'Failed to update the box.');
    }, [updateBoxResponse]);

    useEffect(() => {
        showResponse(refreshFmvResponse, 'Box fmv refreshed.', 'Sorry, there was a problem refreshing the box FMV.');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshFmvResponse]);

    useEffect(() => scrollToTop(), [box]);

    const showMostValuable = useMemo(() => windowDimensions.up(SCREEN.MD), [windowDimensions])

    return (
        <div className={"myCollectionIssues "+(windowDimensions.up(SCREEN.MD) ? 'large-screen' : '')} >

            <Spin spinning={boxQuery.isLoading}>
                {box ? (<>
                    <div
                        className="collection-issues-header"
                        style={{
                            background: `linear-gradient( to top right, rgba(14, 32, 45, 1), rgba(14, 32, 45, 0.5) ), url(${box && box.image_url}) 0 25% no-repeat`,
                            backgroundSize: "cover"
                        }}
                    >
                        <Row className="height-0">
                            <Col span={12} className="top-col-1">
                                <div className="col-1-inner">
                                    <Button type="text" size={windowDimensions.up(SCREEN.MD) ? "large" : 'middle'} className="text-white" onClick={() => navigate('my-collection/?view=boxes')}>
                                        <ArrowLeftOutlined className={windowDimensions.up(SCREEN.MD) ? "font-20" : 'font-12'} /> Back
                                    </Button>
                                </div>
                            </Col>
                            <Col span={12} className="top-col-2">
                                <div className="corner-triangle col-2-inner">
                                    <StorageBoxOutlineIcon
                                        className="whiteCircleIconSmall"
                                        style={
                                            windowDimensions.up(SCREEN.MD) ?
                                                {...iconStyle, border: 'solid 1px #2f404a', marginLeft: "78px", marginTop: "20px"} :
                                                {...iconStyle, border: 'solid 1px #2f404a', marginLeft: "35px", marginTop: "5px"}
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <div className="main-row">
                                <div className="main-row-inner">
                                    <span className="heading-text">{box && box.name}</span><br/>
                                </div>

                            </div>
                        </Row>
                        <Row className="bottom-row">
                            <Col span={24}>
                                <div className="inner-row">
                                    <div className="justify-spacer" />
                                    <div
                                        style={{maxWidth: showMostValuable ? '32%' : '49%'}}
                                        className={"value-cell " + (box.fmv_can_update ? 'cursor-pointer ' : '')}
                                        onClick={(showTotalFmv && box.fmv_can_update) ? async () => {
                                            await postRefreshFmv({box_id: box.id})
                                        } : undefined }
                                    >
                                        <DollarIcon style={iconStyle} className={iconClass} />
                                        <div className={"cell-text mar-left-5 " + (showTotalFmv && box.fmv_can_update && fmvUpdated ? 'mar-top-15 ' : '')}>
                                            Total FMV:&nbsp;

                                            {showTotalFmv ? (
                                                <Spin spinning={refreshFmvResponse.isLoading}>
                                                    <Row className="flex-align-center">
                                                        <FmvPrice
                                                            value={box.fmv}
                                                            stale={box.fmv_is_stale}
                                                            staleInfo="FMV data is stale, please update."
                                                        />
                                                        {box.fmv_can_update && (<SyncOutlined className={'fmv-update can-update-white'} />)}
                                                    </Row>
                                                    {(box.fmv_can_update && fmvUpdated) && (
                                                        <Row className="flex-align-center">
                                                            <span className='font-10 text-grey'>Updated: {formatDateTiny(fmvUpdated)}</span>
                                                        </Row>
                                                    )}
                                                </Spin>
                                            ) : (
                                                <a className="upgrade-link" href="/plans/unlimited/">Upgrade</a>
                                            )}
                                        </div>
                                    </div>
                                    <div className="vertical-line" />
                                    <div
                                        style={{maxWidth: showMostValuable ? '32%' : '49%'}}
                                        className="value-cell"
                                    >
                                        <StorageBoxOutlineIcon style={iconStyle} className={iconClass} />
                                        <div className="cell-text mar-left-5">
                                            Total Items:&nbsp;
                                            {showStats ? (
                                                <b>{box && formatNumber(box.issue_count)}</b>
                                            ) : (
                                                <a className="upgrade-link" href="/plans/unlimited/">Click to Upgrade</a>
                                            )}
                                        </div>
                                    </div>
                                    {showMostValuable && (
                                        <>
                                            <div className="vertical-line"  />
                                            <div
                                                style={{maxWidth: showMostValuable ? '32%' : '49%'}}
                                                className="value-cell"
                                            >
                                                <div className="flex-row most-valuable" >
                                                    <div className="most-valuable-title">Most Valuable</div>
                                                    <div className="most-valuable-vertical-line" />
                                                    <div className={"most-valuable-data mar-left-5 " + (showStats ? '' : 'pad-top-10')}>
                                                        {showStats ? (
                                                            <>
                                                                {box && box.most_valuable && (
                                                                    <>
                                                                        <div className="most-valuable-text mar-bot-5">
                                                                            &nbsp;
                                                                            {box.most_valuable.comic_title}
                                                                        </div>
                                                                        <div className="most-valuable-text">
                                                                            &nbsp;
                                                                            {box.most_valuable.graded_by}&nbsp;
                                                                            {formatGrade(box.most_valuable.grade)}
                                                                            &nbsp;|&nbsp;
                                                                            {formatMoney(box.most_valuable.estval)}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <a className="upgrade-link" href="/plans/unlimited/">Click to Upgrade</a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                    <div className="justify-spacer" />
                                </div>
                            </Col>
                        </Row>
                    </div>

                    <Contain className="pad-bot-20">
                        <MyCollectionIssues showBox={box} onSetBoxImage={onSetBoxImage} search={""} setAddBox={setAddBox}/>
                    </Contain>
                </>) : null }

            </Spin>

            <BoxesAddDrawer visible={addBox.show} issue_ids={addBox.issue_ids} series_ids={addBox.series_ids} onClose={() => {setAddBox({show: false, issue_ids: [], series_ids: []})}} />
        </div>
    );
};

export default MyCollectionBoxIssues;